import { AuthGuard, PermissionGuard } from '@/auth/guard';
import { OrionAccess, OrionModule, OrionSubmodule } from '@/types';
import { SplashScreen } from '@/components/feedback/loading-screen';
import DashboardLayout from '@/layouts/management';
import { Suspense } from 'react';
import { Outlet } from 'react-router';
import {
  TagsEditPage,
  TagsListView,
  CostReportTabs,
  TagsCreatePage,
  ProjectListView,
  CustomerEditPage,
  BillingReportView,
  ResourcesListView,
  AWSAccountTabsView,
  OCITenancyListPage,
  OCITenancyTabsPage,
  ReportApprovalView,
  SubAccountEditPage,
  SubAccountListView,
  SubAccountTabsPage,
  AWSAccountCreatePage,
  CloudSummaryViewPage,
  PayerAccountEditPage,
  PayerAccountTabsPage,
  AzureCustomerTabsPage,
  LinkedAccountEditPage,
  BillingAccountEditPage,
  BillingAccountListView,
  BillingAccountTabsPage,
  PayerAccountReportView,
  BillingAccountCreatePage,
  AzureBillingAccountListView,
  AzureBillingAccountTabsPage,
  LinkedAccountInformationPage,
} from './imports';

export const cloudCompassRoutes = [
  {
    path: 'cloud-compass',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<SplashScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      {
        path: 'summary',
        element: (
          <PermissionGuard
            hasContent
            module={OrionModule.ALTIO}
            submodule={OrionSubmodule.ALTIO.CLOUD_SUMMARY}
            access={OrionAccess.VIEW}
          >
            <CloudSummaryViewPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'cloud/aws/accounts',
        children: [
          {
            path: 'payer-accounts',
            children: [
              {
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.VIEW}
                  >
                    <AWSAccountTabsView />
                  </PermissionGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.VIEW}
                  >
                    <AWSAccountTabsView />
                  </PermissionGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.UPDATE}
                  >
                    <PayerAccountEditPage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.CREATE}
                  >
                    <AWSAccountCreatePage />
                  </PermissionGuard>
                ),
              },
              {
                path: ':id/:tab',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.VIEW}
                  >
                    <PayerAccountTabsPage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'linked-accounts',
                children: [
                  {
                    path: ':id/edit',
                    element: (
                      <PermissionGuard
                        hasContent
                        module={OrionModule.ALTIO}
                        submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                        access={OrionAccess.UPDATE}
                      >
                        <LinkedAccountEditPage />
                      </PermissionGuard>
                    ),
                  },
                  {
                    path: ':id/information',
                    element: (
                      <PermissionGuard
                        hasContent
                        module={OrionModule.ALTIO}
                        submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                        access={OrionAccess.VIEW}
                      >
                        <LinkedAccountInformationPage />
                      </PermissionGuard>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'cloud/gcp/accounts',
        children: [
          {
            path: 'billing-accounts',
            children: [
              {
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.VIEW}
                  >
                    <BillingAccountListView />
                  </PermissionGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.VIEW}
                  >
                    <BillingAccountListView />
                  </PermissionGuard>
                ),
              },
              {
                path: ':id/:tab',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.VIEW}
                  >
                    <BillingAccountTabsPage />
                  </PermissionGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.UPDATE}
                  >
                    <BillingAccountEditPage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.CREATE}
                  >
                    <BillingAccountCreatePage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'subaccounts',
                children: [
                  {
                    element: (
                      <PermissionGuard
                        hasContent
                        module={OrionModule.ALTIO}
                        submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                        access={OrionAccess.VIEW}
                      >
                        <SubAccountListView />
                      </PermissionGuard>
                    ),
                    index: true,
                  },
                  {
                    path: 'list',
                    element: (
                      <PermissionGuard
                        hasContent
                        module={OrionModule.ALTIO}
                        submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                        access={OrionAccess.VIEW}
                      >
                        <SubAccountListView />
                      </PermissionGuard>
                    ),
                  },
                  {
                    path: ':id/:tab',
                    element: (
                      <PermissionGuard
                        hasContent
                        module={OrionModule.ALTIO}
                        submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                        access={OrionAccess.VIEW}
                      >
                        <SubAccountTabsPage />
                      </PermissionGuard>
                    ),
                  },
                  {
                    path: ':id/edit',
                    element: (
                      <PermissionGuard
                        hasContent
                        module={OrionModule.ALTIO}
                        submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                        access={OrionAccess.UPDATE}
                      >
                        <SubAccountEditPage />
                      </PermissionGuard>
                    ),
                  },
                  {
                    path: 'projects',
                    children: [
                      {
                        element: (
                          <PermissionGuard
                            hasContent
                            module={OrionModule.ALTIO}
                            submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                            access={OrionAccess.VIEW}
                          >
                            <ProjectListView />
                          </PermissionGuard>
                        ),
                        index: true,
                      },
                      {
                        path: 'list',
                        element: (
                          <PermissionGuard
                            hasContent
                            module={OrionModule.ALTIO}
                            submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                            access={OrionAccess.VIEW}
                          >
                            <ProjectListView />
                          </PermissionGuard>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'cloud/azure/accounts',
        children: [
          {
            path: 'billing-accounts',
            children: [
              {
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.VIEW}
                  >
                    <AzureBillingAccountListView />
                  </PermissionGuard>
                ),
                index: true,
              },
              {
                path: ':id/:tab',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                    access={OrionAccess.VIEW}
                  >
                    <AzureBillingAccountTabsPage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'customers',
                children: [
                  {
                    path: ':id/:tab',
                    element: (
                      <PermissionGuard
                        hasContent
                        module={OrionModule.ALTIO}
                        submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                        access={OrionAccess.VIEW}
                      >
                        <AzureCustomerTabsPage />
                      </PermissionGuard>
                    ),
                  },
                  {
                    path: ':id/edit',
                    element: (
                      <PermissionGuard
                        hasContent
                        module={OrionModule.ALTIO}
                        submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                        access={OrionAccess.UPDATE}
                      >
                        <CustomerEditPage />
                      </PermissionGuard>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'cloud/oci/tenancies',
        children: [
          {
            element: (
              <PermissionGuard
                hasContent
                module={OrionModule.ALTIO}
                submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                access={OrionAccess.VIEW}
              >
                <OCITenancyListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'list',
            element: (
              <PermissionGuard
                hasContent
                module={OrionModule.ALTIO}
                submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                access={OrionAccess.VIEW}
              >
                <OCITenancyListPage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/:tab',
            element: (
              <PermissionGuard
                hasContent
                module={OrionModule.ALTIO}
                submodule={OrionSubmodule.ALTIO.ACCOUNTS}
                access={OrionAccess.VIEW}
              >
                <OCITenancyTabsPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: 'reports',
        children: [
          {
            path: 'list',
            children: [
              {
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.REPORTS}
                    access={OrionAccess.VIEW}
                  >
                    <CostReportTabs />
                  </PermissionGuard>
                ),
                index: true,
              },
            ],
          },
          {
            path: 'billing',
            children: [
              {
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.REPORTS}
                    access={OrionAccess.VIEW}
                  >
                    <BillingReportView />
                  </PermissionGuard>
                ),
                index: true,
              },
            ],
          },
          {
            path: 'payer-account',
            children: [
              {
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.REPORTS}
                    access={OrionAccess.VIEW}
                  >
                    <PayerAccountReportView />
                  </PermissionGuard>
                ),
                index: true,
              },
            ],
          },
          {
            path: 'approval',
            children: [
              {
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.REPORT_APPROVAL}
                    access={OrionAccess.VIEW}
                  >
                    <ReportApprovalView />
                  </PermissionGuard>
                ),
                index: true,
              },
            ],
          },
        ],
      },
      {
        path: 'resources',
        children: [
          {
            path: 'inventory',
            children: [
              {
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.REPORTS}
                    access={OrionAccess.VIEW}
                  >
                    <ResourcesListView />
                  </PermissionGuard>
                ),
                index: true,
              },
            ],
          },
          {
            path: 'tags',
            children: [
              {
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.REPORTS}
                    access={OrionAccess.VIEW}
                  >
                    <TagsListView />
                  </PermissionGuard>
                ),
                index: true,
              },
              {
                path: ':id/edit',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.REPORTS}
                    access={OrionAccess.UPDATE}
                  >
                    <TagsEditPage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <PermissionGuard
                    hasContent
                    module={OrionModule.ALTIO}
                    submodule={OrionSubmodule.ALTIO.REPORTS}
                    access={OrionAccess.CREATE}
                  >
                    <TagsCreatePage />
                  </PermissionGuard>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
];
